import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { parentApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, BookOpen, FlaskConical, Trophy,
  BarChart3, Clock, Star, ChevronRight, Sun, Moon,
  LogOut, User, GraduationCap, Activity, Award, Target,
  TrendingUp, Calendar, Swords, ClipboardList, X
} from 'lucide-react';

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      const data = await parentApi.getChildren();
      setChildren(data.children || []);
      if (data.children?.length > 0 && !selectedChild) {
        selectChild(data.children[0]);
      }
    } catch {
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  const selectChild = async (child) => {
    setSelectedChild(child);
    setProgressLoading(true);
    try {
      const data = await parentApi.getChildProgress(child.user_id);
      setChildProgress(data);
    } catch {
      toast.error('Failed to load progress');
      setChildProgress(null);
    } finally {
      setProgressLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const p = childProgress;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur" data-testid="parent-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold">Parent Dashboard</h1>
              <p className="text-[10px] text-muted-foreground">Track your child's learning progress</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground" data-testid="parent-logout">
              <LogOut className="w-4 h-4 mr-1.5" />Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">

          {/* Left: Children List */}
          <div className="space-y-4" data-testid="children-panel">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-muted-foreground">My Children</h2>
            </div>

            {children.length === 0 ? (
              <div className="p-8 rounded-2xl border border-dashed border-border text-center" data-testid="no-children">
                <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-1">No children linked yet</p>
                <p className="text-xs text-muted-foreground/60 mb-4 leading-relaxed">
                  Linking is managed by your school. Please contact your school admin and ask them to attach your child to this account.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {children.map(child => (
                  <div key={child.user_id}
                    onClick={() => selectChild(child)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && selectChild(child)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left group cursor-pointer ${
                      selectedChild?.user_id === child.user_id
                        ? 'border-violet-500/50 bg-violet-500/5'
                        : 'border-border bg-card hover:bg-muted/50'
                    }`}
                    data-testid={`child-${child.user_id}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      selectedChild?.user_id === child.user_id ? 'bg-violet-500/20' : 'bg-muted'
                    }`}>
                      <GraduationCap className={`w-5 h-5 ${selectedChild?.user_id === child.user_id ? 'text-violet-400' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{child.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{child.email}</p>
                      {child.grade && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 mt-1 border-border">
                          Class {child.grade}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Progress Details */}
          <div className="space-y-5" data-testid="progress-panel">
            {!selectedChild ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <BarChart3 className="w-16 h-16 text-muted-foreground/20 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">Select a child to view their progress</p>
                <p className="text-sm text-muted-foreground/60">
                  {children.length === 0
                    ? 'Your school admin needs to link your child to this account first.'
                    : ''}
                </p>
              </div>
            ) : progressLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" />
              </div>
            ) : p ? (
              <>
                {/* Child Header */}
                <div className="flex items-center gap-4 pb-4 border-b border-border" data-testid="child-header">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center">
                    <GraduationCap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{p.child.name}</h2>
                    <div className="flex items-center gap-3 mt-0.5">
                      {p.child.grade && <Badge variant="outline" className="text-xs border-border">Class {p.child.grade}</Badge>}
                      {p.child.board && <Badge variant="outline" className="text-xs border-border">{p.child.board}</Badge>}
                      <span className="text-xs text-muted-foreground">{p.child.email}</span>
                    </div>
                  </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-overview">
                  <StatCard icon={FlaskConical} label="Labs Started" value={p.labs.labs_started} color="emerald" />
                  <StatCard icon={Target} label="Missions Done" value={p.labs.total_missions} color="blue" />
                  <StatCard icon={BookOpen} label="Courses" value={`${p.courses.completed}/${p.courses.enrolled}`} color="amber" />
                  <StatCard icon={Star} label="Total XP" value={p.labs.total_xp} color="violet" />
                </div>

                {/* Lab Progress */}
                <Section title="Lab Progress" icon={FlaskConical} color="emerald">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <MiniStat label="Labs Started" value={p.labs.labs_started} />
                    <MiniStat label="Labs Completed" value={p.labs.labs_completed} />
                    <MiniStat label="Total Missions" value={p.labs.total_missions} />
                    <MiniStat label="Total XP" value={p.labs.total_xp} />
                  </div>
                  {p.labs.recent_activity.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Recent Activity</p>
                      <div className="space-y-1.5">
                        {p.labs.recent_activity.map((a, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30">
                            <span className="text-sm font-medium capitalize">{a.lab_id.replace(/-/g, ' ')}</span>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{a.missions} missions</span>
                              <span>{a.xp} XP</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {p.labs.recent_activity.length === 0 && (
                    <p className="text-sm text-muted-foreground/60 text-center py-4">No lab activity yet</p>
                  )}
                </Section>

                {/* Course Progress */}
                <Section title="Course Progress" icon={BookOpen} color="blue">
                  <div className="grid grid-cols-3 gap-3">
                    <MiniStat label="Enrolled" value={p.courses.enrolled} />
                    <MiniStat label="In Progress" value={p.courses.in_progress} />
                    <MiniStat label="Completed" value={p.courses.completed} />
                  </div>
                  {p.courses.enrolled > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-1.5">Completion Rate</p>
                      <Progress value={p.courses.enrolled > 0 ? (p.courses.completed / p.courses.enrolled) * 100 : 0} className="h-2" />
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {p.courses.enrolled > 0 ? Math.round((p.courses.completed / p.courses.enrolled) * 100) : 0}% complete
                      </p>
                    </div>
                  )}
                </Section>

                {/* Exam Performance */}
                <Section title="Exam Performance" icon={ClipboardList} color="amber">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <MiniStat label="Board Exams" value={p.exams.board_exams_taken} />
                    <MiniStat label="Competitive" value={p.exams.competitive_exams_taken} />
                    <MiniStat label="Avg Score" value={`${p.exams.avg_board_score}%`} />
                  </div>
                  {p.exams.recent_board.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Recent Board Exams</p>
                      <div className="space-y-1.5">
                        {p.exams.recent_board.map((e, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30">
                            <span className="text-sm font-medium">{e.subject}</span>
                            <Badge variant="outline" className={`text-xs ${e.total > 0 && (e.score / e.total) >= 0.7 ? 'text-emerald-400 border-emerald-400/30' : 'text-amber-400 border-amber-400/30'}`}>
                              {e.score}/{e.total}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {p.exams.recent_competitive.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-2">Recent Competitive Exams</p>
                      <div className="space-y-1.5">
                        {p.exams.recent_competitive.map((e, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30">
                            <span className="text-sm font-medium">{e.exam_name}</span>
                            <Badge variant="outline" className="text-xs text-blue-400 border-blue-400/30">
                              {e.score}/{e.total}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>

                {/* Achievements & Activity */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Section title="Achievements" icon={Award} color="violet">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <MiniStat label="Points" value={p.gamification.total_points} />
                      <MiniStat label="Badges" value={p.gamification.badges_earned} />
                    </div>
                    {p.gamification.badges.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {p.gamification.badges.map((b, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] border-violet-400/30 text-violet-400 bg-violet-400/5">
                            <Award className="w-2.5 h-2.5 mr-1" />{b.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </Section>

                  <Section title="Activity" icon={Activity} color="cyan">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Last Login</span>
                        <span className="text-sm font-medium">
                          {p.activity.last_login ? new Date(p.activity.last_login).toLocaleDateString() : 'Never'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Sessions (30d)</span>
                        <span className="text-sm font-medium">{p.activity.sessions_30d}</span>
                      </div>
                    </div>
                  </Section>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}

// Reusable components
function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-500/20',
    amber: 'from-amber-500/10 to-amber-500/5 border-amber-500/20',
    violet: 'from-violet-500/10 to-violet-500/5 border-violet-500/20',
  };
  const iconColors = { emerald: 'text-emerald-400', blue: 'text-blue-400', amber: 'text-amber-400', violet: 'text-violet-400' };

  return (
    <div className={`p-4 rounded-2xl border bg-gradient-to-br ${colors[color]}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon className={`w-5 h-5 ${iconColors[color]} mb-2`} />
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-3 rounded-xl bg-muted/30 text-center">
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function Section({ title, icon: Icon, color, children }) {
  const iconColors = { emerald: 'text-emerald-400', blue: 'text-blue-400', amber: 'text-amber-400', violet: 'text-violet-400', cyan: 'text-cyan-400' };
  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColors[color]}`} />{title}
      </h3>
      {children}
    </div>
  );
}
